import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  getDashboardInsights,
  getBudgets,
  getTransactions,
  getUpcomingBills,
  formatCurrency,
  MONTHS,
  TYPE_COLORS,
} from '../lib/api';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Wallet, PiggyBank, ArrowUpRight,
  Plus, ChevronLeft, ChevronRight, RefreshCw, Sparkles, Activity, Command,
  ArrowDownRight, AlertCircle,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import UpcomingBillsBanner from '../components/UpcomingBillsBanner';
import SpendingHeatmap from '../components/SpendingHeatmap';
import EmptyState from '../components/EmptyState';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [insights, setInsights] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [upcomingBills, setUpcomingBills] = useState([]);

  const isCurrentMonth =
    month === new Date().getMonth() + 1 && year === new Date().getFullYear();

  const loadData = async () => {
    setLoading(true);
    try {
      const [insRes, budgetRes, transRes, billsRes] = await Promise.all([
        getDashboardInsights(month, year),
        getBudgets(month, year),
        getTransactions({ month, year }),
        isCurrentMonth ? getUpcomingBills(7) : Promise.resolve({ data: [] }),
      ]);
      setInsights(insRes.data);
      setBudgets(budgetRes.data);
      setRecentTransactions(transRes.data.slice(0, 6));
      setUpcomingBills(billsRes.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y--; }
    else if (m > 12) { m = 1; y++; }
    setMonth(m);
    setYear(y);
  };

  const budgetChartData = useMemo(
    () =>
      budgets
        .filter((b) => b.category_type === 'SPESE' && b.amount > 0)
        .sort((a, b) => (b.actual || 0) - (a.actual || 0))
        .slice(0, 6)
        .map((b) => ({
          name: (b.category_name || '').substring(0, 12),
          budget: b.amount,
          actual: b.actual || 0,
        })),
    [budgets]
  );

  const pieData = useMemo(() => {
    if (!insights) return [];
    return [
      { name: 'Spese', value: insights.totals.SPESE, color: TYPE_COLORS.SPESE.chart },
      { name: 'Risparmi', value: insights.totals.RISPARMI, color: TYPE_COLORS.RISPARMI.chart },
      { name: 'Debiti', value: insights.totals.DEBITI, color: TYPE_COLORS.DEBITI.chart },
    ].filter((d) => d.value > 0);
  }, [insights]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-muted rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-64 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  const cmp = insights?.comparison_previous_month || {};
  const balance = insights?.balance ?? 0;
  const forecast = insights?.forecast || {};

  const DeltaPill = ({ delta, invert = false }) => {
    if (delta === null || delta === undefined) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    const isPositive = delta > 0;
    const isGood = invert ? !isPositive : isPositive; // for SPESE/DEBITI, less is good
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${
          isGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
        }`}
        title={`vs mese precedente`}
      >
        <Icon className="w-3 h-3" />
        {Math.abs(delta).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground flex items-center gap-2">
            Panoramica delle tue finanze
            <kbd className="hidden sm:inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 text-[10px] rounded border bg-muted/40">
              <Command className="w-3 h-3" />K
            </kbd>
            <span className="hidden sm:inline text-xs text-muted-foreground">per il command palette</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="prev-month-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 px-3 py-2 bg-card border rounded-lg min-w-[180px] justify-center">
            <span className="font-medium">{MONTHS[month - 1]} {year}</span>
          </div>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="next-month-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Upcoming bills banner */}
      <UpcomingBillsBanner bills={upcomingBills} />

      {/* KPI cards with comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover" data-testid="income-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Reddito</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(insights?.totals?.REDDITO || 0)}
                </p>
                <DeltaPill delta={cmp.REDDITO?.delta_pct} />
              </div>
              <div className="w-11 h-11 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="expenses-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Spese</p>
                <p className="text-2xl font-bold tabular-nums text-red-500">
                  {formatCurrency(insights?.totals?.SPESE || 0)}
                </p>
                <DeltaPill delta={cmp.SPESE?.delta_pct} invert />
              </div>
              <div className="w-11 h-11 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="savings-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Risparmi</p>
                <p className="text-2xl font-bold tabular-nums text-blue-500">
                  {formatCurrency(insights?.totals?.RISPARMI || 0)}
                </p>
                <DeltaPill delta={cmp.RISPARMI?.delta_pct} />
              </div>
              <div className="w-11 h-11 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="balance-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Saldo</p>
                <p className={`text-2xl font-bold tabular-nums ${balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                  {formatCurrency(balance)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {insights?.transaction_count || 0} transazioni
                </p>
              </div>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${balance >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                <Wallet className={`w-5 h-5 ${balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forecast card (only for current month) */}
      {isCurrentMonth && (
        <Card data-testid="forecast-card" className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Saldo a fine mese</p>
                  <p className={`text-xl font-bold tabular-nums ${forecast.balance_end_of_month >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                    {formatCurrency(forecast.balance_end_of_month || 0)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">previsione</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Spese previste</p>
                  <p className="text-xl font-bold tabular-nums text-red-500/90">
                    {formatCurrency(forecast.spese_end_of_month || 0)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">a questo ritmo</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Spesa media giorno</p>
                  <p className="text-xl font-bold tabular-nums">
                    {formatCurrency(forecast.avg_daily_spend || 0)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {insights?.period?.days_elapsed || 0} di {insights?.period?.days_in_month || 0} giorni
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Heatmap */}
        <Card data-testid="heatmap-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" /> Calendario spese
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SpendingHeatmap heatmap={insights?.heatmap || []} month={month} year={year} />
          </CardContent>
        </Card>

        {/* Top categories */}
        <Card data-testid="top-categories">
          <CardHeader>
            <CardTitle className="text-lg">Top categorie spese</CardTitle>
          </CardHeader>
          <CardContent>
            {insights?.top_categories?.length > 0 ? (
              <div className="space-y-3">
                {insights.top_categories.map((c, i) => {
                  const totalSpese = insights.totals.SPESE || 1;
                  const pct = (c.amount / totalSpese) * 100;
                  return (
                    <div key={c.category_id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate">{i + 1}. {c.name}</span>
                        <span className="tabular-nums text-muted-foreground">{formatCurrency(c.amount)}</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                      <span className="text-[11px] text-muted-foreground tabular-nums">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="Nessuna spesa" description="Aggiungi una transazione per vedere le top categorie." />
            )}
          </CardContent>
        </Card>

        {/* Pie distribution */}
        <Card data-testid="expense-pie-chart">
          <CardHeader>
            <CardTitle className="text-lg">Distribuzione uscite</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {pieData.map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                      <span className="text-xs">{item.name}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyState title="Niente da mostrare" description="Aggiungi spese, risparmi o debiti." />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Budget bars + recent + actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="budget-bar-chart">
          <CardHeader>
            <CardTitle className="text-lg">Budget vs effettivo (Spese)</CardTitle>
          </CardHeader>
          <CardContent>
            {budgetChartData.length > 0 ? (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={budgetChartData} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <XAxis type="number" tickFormatter={(v) => `€${v}`} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(v) => formatCurrency(v)}
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="budget" fill="hsl(var(--muted))" name="Budget" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="actual" fill="hsl(var(--primary))" name="Effettivo" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState
                title="Budget non impostati"
                description="Crea il primo budget per iniziare a monitorare le spese."
                action={{ label: 'Vai al budget', icon: PiggyBank, onClick: () => navigate('/budget') }}
              />
            )}
          </CardContent>
        </Card>

        {/* Quick actions + recent */}
        <div className="space-y-6">
          <Card data-testid="quick-actions">
            <CardHeader>
              <CardTitle className="text-lg">Azioni rapide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start" onClick={() => navigate('/transactions?add=true')} data-testid="quick-add-transaction-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuova transazione
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/budget')} data-testid="quick-budget-btn">
                <PiggyBank className="w-4 h-4 mr-2" /> Gestisci budget
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/import')} data-testid="quick-import-btn">
                <ArrowUpRight className="w-4 h-4 mr-2" /> Importa da Excel
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent transactions */}
      <Card data-testid="recent-transactions">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Ultime transazioni</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/transactions')} data-testid="view-all-transactions-btn">
            Vedi tutte <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentTransactions.length > 0 ? (
            <div className="space-y-1">
              {recentTransactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/40 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${
                      t.type === 'REDDITO' ? 'bg-emerald-500' :
                      t.type === 'SPESE' ? 'bg-red-500' :
                      t.type === 'RISPARMI' ? 'bg-blue-500' : 'bg-orange-500'
                    }`} />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{t.category_name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {t.description || t.date}
                        {t.created_by_name && <span className="ml-2 text-xs">· {t.created_by_name}</span>}
                      </p>
                    </div>
                  </div>
                  <span className={`font-semibold tabular-nums shrink-0 ml-2 ${
                    t.type === 'REDDITO' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
                  }`}>
                    {t.type === 'REDDITO' ? '+' : '-'}{formatCurrency(t.amount)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={AlertCircle}
              title="Nessuna transazione questo mese"
              description="Inizia ad aggiungere transazioni per popolare la dashboard."
              action={{ label: 'Aggiungi', icon: Plus, onClick: () => navigate('/transactions?add=true') }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
